// =============================================================================
// Report Page — Customer-facing status view (R58, R67, R69, R71).
//   R58: Internal view separate from customer view
//   R67: Simpler, high-level customer dashboard
//   R69: Progress against overall target / cumulative
//   R71: Exportable to PDF (print-optimised)
// =============================================================================
import { Printer } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { api } from '../lib/api'
import { useAsync } from '../hooks/useAsync'
import { Card, Button, Skeleton, ErrorState, SectionTitle } from '../components/ui'
import { fmtDate, fmtNum } from '../lib/format'
import type { Insights } from '../lib/types'

export function ReportPage() {
  const { data, loading, error, reload } = useAsync<Insights>(() => api.insights(), [])

  if (loading) return <div className="space-y-4"><SectionTitle>Customer Report</SectionTitle><Skeleton className="h-60" /></div>
  if (error || !data) return <Card className="p-4"><ErrorState message={error || 'Failed'} onRetry={reload} /></Card>

  const { headline, kpi_table, weekly } = data
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <SectionTitle sub="High-level progress for customer / weekly reporting"
        right={<Button size="sm" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Export PDF</Button>}>
        Customer Status Report
      </SectionTitle>

      {/* Header */}
      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-ink">Royal Mail Group — Network Modernisation</h2>
            <p className="text-sm text-muted">Phase 7 Mesh Wi-Fi Migration Programme</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-ink">{today}</p>
            <p className="text-xs text-muted">Generated from live data</p>
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4 lg:grid-cols-4">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted">Sites in Scope</p>
            <p className="mt-1 text-2xl font-bold text-ink">{fmtNum(headline.total_in_scope)}</p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted">Completed</p>
            <p className="mt-1 text-2xl font-bold text-rag-green">{fmtNum(headline.activities_completed)}</p>
            <p className="text-xs text-muted">{headline.pct_complete}%</p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted">Remaining</p>
            <p className="mt-1 text-2xl font-bold text-ink">{fmtNum(headline.remaining)}</p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted">Forecast Finish</p>
            <p className="mt-1 text-2xl font-bold" style={{ color: headline.days_delta <= 0 ? 'var(--rag-green)' : 'var(--rag-red)' }}>{fmtDate(headline.forecast_date)}</p>
            <p className="text-xs text-muted">Target: {fmtDate(headline.target_date)}</p>
          </div>
        </div>
      </Card>

      {/* Progress chart — cumulative MIGRATIONS vs the migration plan. Named explicitly as
          migrations so it doesn't read as the "Completed" (closed-out, T+15) headline above:
          a site is migrated before its activities are complete, so this actual (migrations)
          runs ahead of the Completed figure. */}
      <Card className="p-6">
        <h3 className="mb-1 text-[14px] font-semibold text-ink">Migrations Against Plan</h3>
        <p className="mb-4 text-xs text-muted">Cumulative sites migrated vs plan. Migrations run ahead of “Completed” above, which counts sites fully closed out (through T+15).</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={weekly.filter((w) => w.cumulative_actual > 0 || w.cumulative_planned > 0)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="week_start" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip labelFormatter={(l) => `Week of ${fmtDate(l)}`} />
            <Bar dataKey="cumulative_planned" name="Planned migrations (cumulative)" fill="#e2e8f0" />
            <Bar dataKey="cumulative_actual" name="Migrations (cumulative)" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* KPI table */}
      <Card className="p-6">
        <h3 className="mb-4 text-[14px] font-semibold text-ink">Delivery KPIs</h3>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr><th className="pb-2">Metric</th><th className="pb-2 text-right">Target</th><th className="pb-2 text-right">Actual</th><th className="pb-2 text-right">Progress</th></tr>
          </thead>
          <tbody>
            {kpi_table.map((k) => {
              // planned is null when the KPI has no seeded plan curve — neutral target, no progress bar.
              const hasPlan = k.planned != null && k.planned > 0
              const pct = hasPlan ? Math.round((k.actual / (k.planned as number)) * 100) : null
              return (
                <tr key={k.key} className="border-b border-line/60">
                  <td className="py-2 font-medium text-ink">{k.label}</td>
                  <td className="py-2 text-right tabular-nums text-muted">{k.planned == null ? '—' : fmtNum(k.planned)}</td>
                  <td className="py-2 text-right font-semibold tabular-nums text-ink">{fmtNum(k.actual)}</td>
                  <td className="py-2 text-right">
                    {pct == null ? <span className="text-xs text-muted">—</span> : (
                      <div className="inline-flex items-center gap-2">
                        <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-brand-600" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <span className="text-xs tabular-nums text-muted">{pct}%</span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>

      {/* Footer */}
      <p className="text-center text-[10px] text-muted">
        This report is generated from the RMG Site Delivery Portal. Figures are live as of the time of generation.
      </p>
    </div>
  )
}
